import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  MailMessage,
  MailParticularData,
  SendMessageRequest
} from '../../models/mail';
import {
  Document,
    Policy,
  PolicyExpedient,
  PolicyReceipt,
  PolicySinister
} from '../../models/policy';
import { PolicyOther } from '../../models/policy-other';
import { PendingReceipt } from '../../models/receipt';
import { AuthMirrorService } from '../core/auth/auth-mirror.service';
import { BaseService } from '../core/base.service';
import { OperationRegisterService } from '../core/operation-register.service';
import { BaseMailService } from './base-mail.service';

@Injectable({
  providedIn: 'root'
})
export class OtherPoliciesService {
  constructor(
    private http: HttpClient,
    private base: BaseService,
    private baseMailService: BaseMailService,
    private authMirror: AuthMirrorService,
    private or: OperationRegisterService
  ) {}

  getOtherPolicy({ policyNumber, type }: PolicyOther): Observable<PolicyOther> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${policyNumber}`;
    return this.http.get<PolicyOther>(
      url,
      this.base.getPrivateOIMHeaders({ cacheResponse: true })
    );
  }

  getOtherReceipts(policy: PolicyOther): Observable<PolicyReceipt[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${
      policy.policyNumber
    }/receipts?companyId=${policy.companyId}`;
    const params = this.or.setOtherOperationParams(policy);
    return this.http.get<PolicyReceipt[]>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params
    });
  }

  getOtherReceipt(
    policy: PolicyOther,
    receiptNumber: string
  ): Observable<PolicyReceipt> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${
      policy.policyNumber
    }/receipts/${receiptNumber}?companyId=${policy.companyId}`;
    const params = this.or.setOtherOperationParams(policy);
    return this.http.get<PolicyReceipt>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params
    });
  }

  getOtherPendingReceipts(
    policyNumber: string,
    companyId: string,
    params: any
  ): Observable<PendingReceipt[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${policyNumber}/receiptsPayment/receipts?companyId=${companyId}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<PendingReceipt[]>(url, { ...headers, params });
  }

  getOtherSinisters(policy: PolicyOther): Observable<PolicySinister[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${
      policy.policyNumber
    }/sinisters`;
    const params = this.or.setOtherOperationParams(policy);
    return this.http.get<PolicySinister[]>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params
    });
  }

  getOtherExpedients(
    policy: PolicyOther,
    sinisterCode: string
  ): Observable<PolicyExpedient[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${
      policy.policyNumber
    }/sinisters/${sinisterCode}/expedients`;
    const params = this.or.setOtherOperationParams(policy);
    return this.http.get<PolicyExpedient[]>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params
    });
  }

  getOtherReceiptDocument(
    policyNumber: string,
    companyId: string,
    receiptNumber: string,
    params: HttpParams
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${policyNumber}/documents/receipts/${receiptNumber}?companyId=${companyId}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, { ...headers, params });
  }

  getOtherParticularConditions(
    policyNumber: string,
    params?: HttpParams
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${policyNumber}/documents/particularConditions`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, { ...headers, params });
  }

  getOtherGeneralConditions(
    policyNumber: string,
    params: HttpParams,
    generalConditionsCode?: string
  ): Observable<Document> {
    if (generalConditionsCode) {
      params = params.append(
        'generalConditionsCode',
        generalConditionsCode
      );
    }
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${policyNumber}/documents/generalConditions`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, { ...headers, params });
  }

  getOtherRenewalNotice(policy: Policy): Observable<Document> {
    const { policyNumber: homePolicyNumber } = policy || {};
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${homePolicyNumber}/documents/renewalNotice`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    const params = this.or.calculateDocumentROParams(policy);
    return this.http.get<Document>(
      url,
      { ...headers, params }
    );
  }

  sendMessageMoreInfoSinister(
    name: string,
    policy: string,
    phone: string,
    email: string,
    nif: string,
    query: string
  ) {
    const { user } = this.authMirror.authService.getState();
    const userIsCompany = this.authMirror.authService.checkUserIsCompany();
    const userEmail = userIsCompany ? user.legalRepresentative.email : user.email;
    const msg: MailMessage = {
      sendingFile: false,
      read: true,
      mailBoxId: '2',
      inboxId: '1',
      responseModeId: '1',
      type: '0',
      frmId: '287',
      groupId: '264',
      from: userEmail ? userEmail : null
    };
    const data: MailParticularData[] = [];
    data.push({ field: 'nombre', value: name });
    data.push({ field: 'poliza', value: policy });
    data.push({ field: 'telefono', value: phone });
    data.push({ field: 'email', value: email });
    data.push({ field: 'nif', value: nif });
    data.push({ field: 'consulta', value: query });
    const request: SendMessageRequest = {
      mailboxIdentifier: user.documentNumber,
      particularData: data,
      message: msg
    };
    return this.baseMailService.sendMessage(request);
  }
}
